import axios, { AxiosError } from "axios";
import createAuthRefreshInterceptor from "axios-auth-refresh";
import { authActions } from "@/store/reducers/auth.reducer";
import { store } from "../../store/root";
import { HttpClient } from "./HttpClient";
import { AppConfig } from "../config";
import { ErrorResponseType } from "@/models/common/error";

export class HttpClientFactory {
  constructor(private configs: AppConfig) {}

  create(): HttpClient {
    const instance = axios.create({
      baseURL: this.configs.platformUri
    });

    // --- REQUEST INTERCEPTOR ---
    instance.interceptors.request.use((config) => {
      const {
        auth: { accessToken }
      } = store.getState();

      if (accessToken && config.headers) {
        config.headers.Authorization = `Bearer ${accessToken}`;
        config.headers.set("Access-Control-Allow-Origin", "*");
      }

      return config;
    });

    // --- REFRESH TOKEN INTERCEPTOR ---
    createAuthRefreshInterceptor(
      instance,
      async (failedRequest) => {
        const {
          auth: { refreshToken }
        } = store.getState();

        try {
          const { data } = await axios.post<{ accessToken: string; refreshToken: string }>(
            `${this.configs.platformUri}/auth/refresh`,
            { refreshToken }
          );

          store.dispatch(authActions.updateAccessToken(data.accessToken));
          store.dispatch(authActions.updateRefreshToken(data.refreshToken));

          // Update the failed request with new token
          if (failedRequest.response?.config?.headers) {
            failedRequest.response.config.headers.Authorization = `Bearer ${data.accessToken}`;
          }

          return Promise.resolve();
        } catch (error: any) {
          // If refresh fails, logout the user
          store.dispatch(authActions.signOut());
          window.location.href = "/auth/signin";
          return Promise.reject(error);
        }
      },
      {
        statusCodes: [401],
        pauseInstanceWhileRefreshing: true
      }
    );

    // --- RESPONSE ERROR INTERCEPTOR ---
    instance.interceptors.response.use(
      (response) => response,
      (error: AxiosError<ErrorResponseType>) => {
        if (error.response?.data) {
          // Ensure we always throw a normalized ErrorResponse
          return Promise.reject(error.response.data as ErrorResponseType);
        }

        // Fallback default error if response is missing
        const fallbackError: ErrorResponseType = {
          statusCode: error.response?.status ?? 500,
          timestamp: new Date().toISOString(),
          path: error.config?.url ?? "",
          method: error.config?.method?.toUpperCase() ?? "",
          message: { ar: error.message, en: error.message }
        } as ErrorResponseType;

        return Promise.reject(fallbackError);
      }
    );

    return instance;
  }

  createSubscriptionClient(): HttpClient {
    const instance = axios.create({
      baseURL: this.configs.subscriptionServiceUri
    });

    // --- REQUEST INTERCEPTOR ---
    instance.interceptors.request.use((config) => {
      const {
        auth: { accessToken }
      } = store.getState();

      if (accessToken && config.headers) {
        config.headers.Authorization = `Bearer ${accessToken}`;
        config.headers.set("Access-Control-Allow-Origin", "*");
      }

      return config;
    });

    // --- RESPONSE ERROR INTERCEPTOR ---
    instance.interceptors.response.use(
      (response) => response,
      (error: AxiosError<ErrorResponseType>) => {
        if (error.response?.data) {
          return Promise.reject(error.response.data as ErrorResponseType);
        }

        const fallbackError: ErrorResponseType = {
          statusCode: error.response?.status ?? 500,
          timestamp: new Date().toISOString(),
          path: error.config?.url ?? "",
          method: error.config?.method?.toUpperCase() ?? "",
          message: { ar: error.message, en: error.message }
        } as ErrorResponseType;

        return Promise.reject(fallbackError);
      }
    );

    return instance;
  }
}
